import { useModalActions, useModalState } from 'components/ModalManager/Context';
import { useOrganizationInvitesList } from 'modules/Organizations/hooks';
import { useEffect, useMemo } from 'react';
import { useAuthState } from 'modules/Auth/AuthContext';
import { appStorage } from 'utils/appStorage';
import { useRouter } from 'next/router';
import { onboardingDialogBackdropClassName } from 'modules/Auth/components/OnboardingDialog/OnboardingDialog.styles';
import { dialogComponents } from 'components/Dialog/dialogComponents';
import { noop } from 'utils/fp';
import { centerize } from './JoinOrganization.styles';
import { useAcceptOrgInvitation, useDeclineOrgInvitation } from './hooks';
import { OrgInvitationCard } from '../OrgInvitation/OrgInvitationCard';

const JOIN_ORG_MODAL_ID = 'join-org-modal';

type JoinOrganizationModalProps = {
  onCloseModal: () => void;
  invitation: CF.API.Organizations.OrgMemberInvite;
  acceptInvitation: ReturnType<typeof useAcceptOrgInvitation>;
  declineInvitation: ReturnType<typeof useDeclineOrgInvitation>;
};

export const JoinOrganizationModal: React.FC<JoinOrganizationModalProps> = ({ invitation, acceptInvitation, declineInvitation, onCloseModal }) => {
  return (
    <div className={centerize}>
      <OrgInvitationCard
        acceptInvitation={acceptInvitation}
        declineInvitation={declineInvitation}
        invitation={invitation}
        closeButton={<dialogComponents.CloseButton onClick={onCloseModal} />}
      />
    </div>
  );
};

export function useJoinOrganizationModal() {
  const { openModal, closeModal } = useModalActions();
  const joinOrgModalState = manageJoinOrgModalLocalStorage();

  const closeJoinOrgModal = () => {
    closeModal({ id: JOIN_ORG_MODAL_ID });
  };

  const closeAndHideJoinOrgModal = () => {
    closeJoinOrgModal();
    joinOrgModalState.hide();
  };

  const acceptInvitation = useAcceptOrgInvitation(closeJoinOrgModal);

  const declineInvitation = useDeclineOrgInvitation(closeJoinOrgModal);

  const openJoinOrgModal = (invitation: CF.API.Organizations.OrgMemberInvite) => {
    openModal({
      id: JOIN_ORG_MODAL_ID,
      title: '',
      content: (
        <JoinOrganizationModal
          onCloseModal={closeAndHideJoinOrgModal}
          invitation={invitation}
          acceptInvitation={acceptInvitation}
          declineInvitation={declineInvitation}
        />
      ),
      backdropClassName: onboardingDialogBackdropClassName,
      makeActions: noop,
    });
  };

  return openJoinOrgModal;
}

export function useOpenJoinOrganizationModalWhenInvited() {
  const { authData } = useAuthState();
  const { modals } = useModalState();
  const orgInvitesList = useOrganizationInvitesList();
  const openJoinOrgModal = useJoinOrganizationModal();
  const joinOrgModalState = manageJoinOrgModalLocalStorage();
  const router = useRouter();

  const areOtherModalsOpen = modals?.length > 0;
  const isUserOnboarded = Boolean(authData) && authData?.is_email_verified && !authData?.is_onboarding_pending;
  const isOnJoinOrgPage = router?.pathname === '/join_org';
  const isModalHidden = joinOrgModalState.isHidden();
  const canJoinOrgModalBeShown = useMemo(
    () => isUserOnboarded && !areOtherModalsOpen && !isOnJoinOrgPage && !isModalHidden,
    [isUserOnboarded, isOnJoinOrgPage, areOtherModalsOpen, isModalHidden],
  );

  const showInvitation = orgInvitesList?.[0];

  useEffect(() => {
    if (canJoinOrgModalBeShown && showInvitation) {
      openJoinOrgModal(showInvitation);
    }
  }, [showInvitation, canJoinOrgModalBeShown]);
}

export function manageJoinOrgModalLocalStorage() {
  return {
    isHidden: () => {
      const stored = appStorage.get('hideJoinOrgModal');
      return !!stored;
    },
    hide: () => {
      appStorage.set('hideJoinOrgModal', true);
    },
    clear: () => {
      appStorage.rm('hideJoinOrgModal');
    },
  };
}
